import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { BookOpen, Heart, Sparkles, Users, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EducationPage = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/education`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(content.map(c => c.category))];
  
  const filteredContent = selectedCategory === 'all' 
    ? content 
    : content.filter(c => c.category === selectedCategory);

  const categoryIcons = {
    'Positions': <Users className="w-5 h-5" />,
    'Foreplay': <Heart className="w-5 h-5" />,
    'Connection': <Sparkles className="w-5 h-5" />,
    'Techniques': <BookOpen className="w-5 h-5" />,
    'Enhancement': <Sparkles className="w-5 h-5" />
  };

  const categoryColors = {
    'Positions': 'bg-rose-50 text-rose-600',
    'Foreplay': 'bg-amber-50 text-amber-600',
    'Connection': 'bg-purple-50 text-purple-600',
    'Techniques': 'bg-blue-50 text-blue-600',
    'Enhancement': 'bg-green-50 text-green-600'
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="education-page">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold mb-2">Educational Content</h1>
          <p className="text-muted-foreground">
            Wisdom from the Kama Sutra to enhance intimacy and connection
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
              data-testid={`filter-${category}`}
            >
              {category === 'all' ? 'All Topics' : category}
            </button>
          ))}
        </div>

        {/* Content Cards */}
        <div className="space-y-6">
          {filteredContent.map((item) => (
            <Card 
              key={item.id} 
              className="rounded-2xl border-border/50 shadow-soft overflow-hidden"
              data-testid={`content-card-${item.id}`}
            >
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${categoryColors[item.category] || 'bg-gray-50 text-gray-600'}`}>
                      {categoryIcons[item.category] || <BookOpen className="w-5 h-5" />}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-serif">{item.title}</CardTitle>
                      <Badge variant="secondary" className="mt-1 rounded-full">
                        {item.category}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {item.content}
                </p>

                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="tips" className="border-none">
                    <AccordionTrigger className="text-primary hover:no-underline py-2">
                      View Tips & Techniques
                    </AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-3 pt-2">
                        {item.tips.map((tip, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center shrink-0 mt-0.5">
                              {index + 1}
                            </span>
                            <span className="text-muted-foreground">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Suitable for:</span> {item.suitable_for_sizes}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredContent.length === 0 && (
          <Card className="rounded-2xl border-border/50 shadow-soft">
            <CardContent className="py-16 text-center">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-serif font-medium mb-2">No content found</h3>
              <p className="text-muted-foreground">
                Try selecting a different category or check back later.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Disclaimer */}
        <Card className="rounded-2xl border-border/50 shadow-soft mt-8 bg-muted/30">
          <CardContent className="py-6">
            <p className="text-sm text-muted-foreground text-center">
              <strong>Note:</strong> This educational content is inspired by ancient wisdom and modern relationship science. 
              Always prioritize consent, communication, and mutual respect in all intimate encounters.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EducationPage;
